// OML renderer — converts an OmlNode tree to an HTML string.
// Separating rendering from tree construction enables caching, diffing, and
// multi-target output (HTML, email, etc.) from the same tree.

import type { OmlNode, OmlProps } from './types.js';

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};
const HTML_ESCAPE_RE = /[&<>"']/g;

function escapeHtml(s: string): string {
  return s.replace(HTML_ESCAPE_RE, (c) => HTML_ESCAPES[c]);
}

function styleToString(style: Record<string, string | number>): string {
  return Object.entries(style)
    .map(([k, v]) => {
      const prop = k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
      return `${prop}:${v}`;
    })
    .join(';');
}

function renderProps(props: OmlProps | null | undefined): string {
  if (!props) return '';
  const parts: string[] = [];

  for (const [key, val] of Object.entries(props)) {
    if (key === 'key' || key === 'ref' || key === 'dangerouslySetInnerHTML') continue;
    if (val === false || val == null) continue;

    const attr =
      key === 'className'
        ? 'class'
        : key === 'htmlFor'
          ? 'for'
          : key === 'tabIndex'
            ? 'tabindex'
            : key;

    if (attr.startsWith('on') && attr[2] === attr[2]?.toUpperCase()) continue;

    if (val === true) {
      parts.push(attr);
      continue;
    }

    if (attr === 'style') {
      const css =
        typeof val === 'string' ? val : styleToString(val as Record<string, string | number>);
      parts.push(`style="${escapeHtml(css)}"`);
      continue;
    }

    parts.push(`${attr}="${escapeHtml(String(val))}"`);
  }

  return parts.length ? ` ${parts.join(' ')}` : '';
}

/** Render an OmlNode tree to an HTML string. */
export function renderToHtml(node: OmlNode): string {
  if (node === null) return '';
  if (typeof (node as unknown) === 'string') return node as unknown as string;

  if (node.type === '#text') return escapeHtml(node.value);

  if (node.type === '#fragment') return node.children.map(renderToHtml).join('');

  if (node.type === '#component') return renderToHtml(node.output);

  if (node.type === '#raw') return node.value;

  // OmlElement
  const { tag, props, children } = node;
  const attrs = renderProps(props);

  if (VOID_ELEMENTS.has(tag)) return `<${tag}${attrs}>`;

  const dsi = (props as { dangerouslySetInnerHTML?: { __html: string } } | null | undefined)
    ?.dangerouslySetInnerHTML;
  if (dsi) {
    return `<${tag}${attrs}>${dsi.__html}</${tag}>`;
  }

  return `<${tag}${attrs}>${children.map(renderToHtml).join('')}</${tag}>`;
}

function injectOmlAttrs(html: string, name: string, inst: number): string {
  return html.replace(/(<[a-zA-Z][a-zA-Z0-9-]*)(\s|>|\/)/, (_, tag, after) => {
    return `${tag} data-oml-comp="${name}" data-oml-inst="${inst}"${after}`;
  });
}

type DevCounters = Map<string, number>;

/**
 * For #raw-returning components whose JSX children are stored on the node, inject
 * child markers into the parent's raw HTML via string replacement. Each child's
 * original raw HTML is found in the parent string and replaced with the marked version.
 * This works because library components include their children's HTML verbatim.
 */
function injectChildMarkers(
  parentHtml: string,
  children: OmlNode[],
  counters: DevCounters,
): string {
  let html = parentHtml;
  for (const child of children) {
    if (!child) continue;
    if (child.type === '#component' && child.output?.type === '#raw') {
      const childInst = counters.get(child.name) ?? 0;
      counters.set(child.name, childInst + 1);
      let childHtml = child.output.value;
      if (child.children && child.children.length > 0) {
        childHtml = injectChildMarkers(childHtml, child.children, counters);
      }
      const markedChildHtml = injectOmlAttrs(childHtml, child.name, childInst);
      html = html.replace(child.output.value, markedChildHtml);
    } else if (child.type === '#fragment') {
      html = injectChildMarkers(html, child.children, counters);
    }
  }
  return html;
}

/**
 * Dev-mode renderer — identical to renderToHtml but injects `data-oml-comp` and
 * `data-oml-inst` attributes into the first HTML tag of #raw-returning components
 * and recursively into their JSX children. This lets the visual editor map clicked
 * DOM elements back to their OML #component nodes.
 */
export function renderToHtmlDev(node: OmlNode, counters: DevCounters = new Map()): string {
  if (node === null) return '';
  if (typeof (node as unknown) === 'string') return node as unknown as string;

  if (node.type === '#text') return escapeHtml(node.value);

  if (node.type === '#fragment')
    return node.children.map((c) => renderToHtmlDev(c, counters)).join('');

  if (node.type === '#raw') return node.value;

  if (node.type === '#component') {
    const inst = counters.get(node.name) ?? 0;
    counters.set(node.name, inst + 1);
    if (node.output?.type === '#raw') {
      let html = node.output.value;
      if (node.children && node.children.length > 0) {
        html = injectChildMarkers(html, node.children, counters);
      }
      return injectOmlAttrs(html, node.name, inst);
    }
    return renderToHtmlDev(node.output, counters);
  }

  const { tag, props, children } = node;
  const attrs = renderProps(props);

  if (VOID_ELEMENTS.has(tag)) return `<${tag}${attrs}>`;

  const dsi = (props as { dangerouslySetInnerHTML?: { __html: string } } | null | undefined)
    ?.dangerouslySetInnerHTML;
  if (dsi) return `<${tag}${attrs}>${dsi.__html}</${tag}>`;

  return `<${tag}${attrs}>${children.map((c) => renderToHtmlDev(c, counters)).join('')}</${tag}>`;
}
